<!-- @format -->
<template>
    <div class="mdf-details-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Details</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="isLoading" />
                <mdf-details-table
                    v-else
                    :mdf-request-main-details="mdfRequestMainDetails"
                    :request-details="requestDetails"
                />
                <div class="pop-divider" />
                <div
                    v-for="(allocatedFund, index) in mdfRequestMainDetails.allocations"
                    :key="index"
                    class="allocated-fund"
                >
                    <pop-card-details
                        :item="allocatedFund.cardDetails"
                        isDragAndDropDisabled="true"
                    >
                    </pop-card-details>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div v-if="!actionsDisable" class="action-container">
                    <app-button v-if="editAction.enable" @click="editAction.action"
                        >Edit</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const MDFDetailsTable = () => import('@/components/sliding-widgets/mdf/details/mdf-details-table');
const MDFPopCardDetails = () =>
    import('@/components/sliding-widgets/mdf/submit-pop/pop-card-details');

import { MDFAgencyRequestMixin } from '@/mixins/mdf-agency-request-mixin';
import LoadingBoundaryMixin from '@/mixins/loading-boundary-mixin';
import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'mdf-details-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'mdf-details-table': MDFDetailsTable,
        'pop-card-details': MDFPopCardDetails,
    },

    mixins: [MDFAgencyRequestMixin, LoadingBoundaryMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            mdfRequestMainDetails: {},
            actionsDisable: this.widget.actionsDisable,
            requestDetails: [],
        };
    },

    computed: {
        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        isClientPage() {
            return this.$store.state.context.client.id;
        },

        mdfId() {
            return this.widget.options.mdfRequest.id;
        },

        vendorId() {
            return this.widget.options.mdfRequest.vendor.id;
        },

        clientId() {
            return this.widget.options.clientId;
        },

        vendor() {
            return this.widget.options.mdfRequest.vendor;
        },

        requestMadeBy() {
            return this.widget.options.mdfRequest.requestMadeBy;
        },

        accountClientId() {
            return this.$store.state.context.account.id;
        },

        editAction() {
            const shouldEditPop = ['POP Submitted', 'Pending Payment', 'POP Denied'];
            const shouldSubmitPop = ['Funds Allocated'];
            const shouldDisableAction = ['Pending', 'Expired', 'Funds Paid', 'Denied'];
            const requestStatus = this.mdfRequestMainDetails.status;
            if (shouldEditPop.includes(requestStatus)) {
                return {
                    enable: true,
                    action: () => this.openSubmitPop(true),
                };
            }

            if (shouldSubmitPop.includes(requestStatus)) {
                return {
                    enable: true,
                    action: this.openSubmitPop,
                };
            }

            if (shouldDisableAction.includes(requestStatus)) {
                return {
                    enable: false,
                    action: () => null,
                };
            }

            return {
                enable: false,
                action: () => null,
            };
        },
    },

    created() {
        this.initializeWidget();
    },

    methods: {
        initializeWidget() {
            if (this.mdfId !== undefined) {
                const getDataProcess = () => {
                    return new Promise((resolve, reject) => {
                        this.getWidgetRequestDetails(
                            this.vendorId,
                            this.mdfId,
                            this.isClientPage,
                            this.vendor,
                            this.requestMadeBy,
                            this.clientId,
                        )
                            .then(({ requestDetails, mdfRequestMainDetails }) => {
                                this.mdfRequestMainDetails = mdfRequestMainDetails;
                                this.requestDetails = requestDetails;
                                this.requestDetails = this.requestDetails.filter(({ content }) => {
                                    if (Array.isArray(content)) {
                                        return content.length > 0;
                                    }
                                    return !!content;
                                });

                                resolve();
                            })
                            .catch((error) => {
                                this.$helpers.showFailNotification(
                                    "An error ocurred trying to get the request's details.",
                                    { error },
                                );
                                reject();
                            });
                    });
                };

                this.withLoadingBoundary(getDataProcess);
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        openSubmitPop(isEdit) {
            if (this.isClientPage) {
                EventBus.$emit('open-widget', {
                    name: 'mdf-submit-pop-widget',
                    mdfRequestId: this.mdfId,
                    accountClientId: this.accountClientId,
                    edit: isEdit,
                });
                return;
            }
            this.$router.push(`${this.baseUrl}/MDF/Requests/${this.mdfId}/Funds/Allocate`);
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.mdf-details-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    color: #382153;
    padding: 0 40px;
}

.mdf-details-widget .action-container .btn {
    padding: 0 40px;
}

.mdf-details-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}
</style>
